/**
 * The move to a new address (S30, docs/umzug-mzapps-cloud.md): for six weeks the old address works
 * as before, but says where SpeiseKarte lives now. Password managers and passkeys know only the old
 * address, so one signs in there and is taken along, signed in („Übergabe“, server: move.ts); under
 * the new address the app then asks for the password once, so the password manager keeps it there
 * too, and offers a passkey for the new address.
 */
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { account, cancelled, deviceName, passkeysSupported, serverFeatures, siteInfo } from "../account.ts";
import type { Site } from "../account.ts";
import { messageOf, store } from "../store.ts";
import type { SignedIn } from "../store.ts";
import { AuthPage, Login, authMessage } from "./Login.tsx";

const hostOf = (origin: string) => new URL(origin).host;

export function useSite(): Site | null {
  const [site, setSite] = useState<Site | null>(null);
  useEffect(() => void siteInfo().then(setSite, () => setSite(null)), []);
  return site;
}

/** To the new address, signed in: the key comes from the server, the browser goes there. */
export async function handOver(): Promise<void> {
  location.replace((await account.move()).url);
}

/** Under an old address: where SpeiseKarte lives now. Signed in, the button takes the sign-in along. */
export function MovedBanner({ signedIn }: { signedIn: boolean }) {
  const site = useSite();
  const [error, setError] = useState<string | null>(null);
  if (!site?.moved) return null;
  const go = () => handOver().catch((err) => setError(messageOf(err)));
  return (
    <div className="banner moved" data-testid="moved-banner">
      <span>
        SpeiseKarte ist umgezogen: <a href={site.origin}>{hostOf(site.origin)}</a>. Bitte dort {signedIn ? "weiterarbeiten" : "anmelden"} und neu zum Home-Bildschirm hinzufügen.
        {!signedIn && " Wer sich hier anmeldet, kommt angemeldet dorthin."}
        {error && <span className="error"> {error}</span>}
      </span>
      {signedIn && (
        <button onClick={() => void go()} data-testid="moved-go">
          Zur neuen Adresse
        </button>
      )}
    </div>
  );
}

/** On the sign-in under the new address: the password may still be kept under the old one. */
export function OldPasswordHint() {
  const site = useSite();
  if (!site?.oldOrigin || site.moved) return null;
  return (
    <div className="moved-hint" data-testid="old-password-hint">
      <p className="hint">Dein Passwort ist noch unter der alten Adresse gespeichert? Dort anmelden, du kommst angemeldet hierher zurück.</p>
      <a className="button wide center" href={`${site.oldOrigin}/#/umziehen`} data-testid="old-password-go">
        Unter {hostOf(site.oldOrigin)} anmelden
      </a>
    </div>
  );
}

/** `#/umziehen` under the old address: signed in, straight to the new one; otherwise sign in first. */
export function MoveOver({ onSignedIn }: { onSignedIn: (s: SignedIn) => void }) {
  const [asking, setAsking] = useState(!store.session);
  useEffect(() => {
    if (store.session) handOver().catch(() => setAsking(true));
  }, []);
  return asking ? <Login onSignedIn={onSignedIn} /> : <p className="loading">Weiter zur neuen Adresse …</p>;
}

/**
 * `#/umzug/<key>` under the new address: signs in with the key, then the password once (for the
 * password manager) and a passkey, both skippable.
 */
export function Arrive({ moveKey, onSignedIn }: { moveKey: string; onSignedIn: (s: SignedIn) => void }) {
  const [signedIn, setSignedIn] = useState<SignedIn | null>(null);
  const [step, setStep] = useState<"password" | "passkey">("password");
  const [error, setError] = useState<string | null>(null);
  const taken = useRef(false);
  useEffect(() => {
    if (taken.current) return;
    taken.current = true;
    // The key is used up once read; it does not stay in the address bar or the history.
    history.replaceState(null, "", "#/umzug");
    account.redeem(moveKey).then(setSignedIn, (err) => setError(authMessage(err)));
  }, [moveKey]);

  const done = () => onSignedIn(signedIn!);
  const afterPassword = async () => {
    const offer = await serverFeatures().then((f) => f.passkeys && passkeysSupported(), () => false);
    if (offer) setStep("passkey");
    else done();
  };

  if (error) {
    return (
      <AuthPage>
        <div className="card" data-testid="arrive-error">
          <h2>Anmelden</h2>
          <p className="error-box">{error}</p>
          <a className="button wide center" href="#/anmelden">
            Zur Anmeldung
          </a>
        </div>
      </AuthPage>
    );
  }
  if (!signedIn) return <p className="loading">Anmelden …</p>;
  return (
    <AuthPage>
      {step === "password" ? <SavePassword signedIn={signedIn} onDone={() => void afterPassword()} /> : <NewPasskey onDone={done} />}
    </AuthPage>
  );
}

/**
 * „Passwort einmal eingeben“: a real sign-in form (user name, current password), so the password
 * manager offers to keep the password for this address once it is sent and the form is gone.
 */
function SavePassword({ signedIn, onDone }: { signedIn: SignedIn; onDone: () => void }) {
  const [username, setUsername] = useState(signedIn.user.username);
  const [password, setPassword] = useState("");
  const [state, setState] = useState<{ busy?: boolean; error?: string }>({});
  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!password) return setState({ error: "Bitte gib dein Passwort ein." });
    setState({ busy: true });
    try {
      await account.checkPassword(password);
      onDone();
    } catch (err) {
      setState({ error: messageOf(err) });
    }
  }
  return (
    <div className="card" data-testid="save-password">
      <h2>Hallo {signedIn.user.displayName}!</h2>
      <p className="ok-text">Du bist unter der neuen Adresse angemeldet.</p>
      <p>Damit dein Passwort-Manager dich hier wiedererkennt: Passwort einmal eingeben.</p>
      <form noValidate onSubmit={submit}>
        <label>
          Benutzername
          <input value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="save-username" />
        </label>
        <label>
          Passwort
          <input type="password" value={password} onChange={(e) => [setPassword(e.target.value), setState({})]} autoComplete="current-password" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="save-password-input" />
        </label>
        {state.error && <p className="error-box">{state.error}</p>}
        <button className="primary" disabled={state.busy} data-testid="save-password-send">
          {state.busy ? "Wird geprüft …" : "Speichern lassen"}
        </button>
      </form>
      <button className="link wide" onClick={onDone} data-testid="save-password-skip">
        Überspringen
      </button>
    </div>
  );
}

/** A passkey belongs to one address: the old one's does not work here, so offer a new one. */
function NewPasskey({ onDone }: { onDone: () => void }) {
  const [state, setState] = useState<{ busy?: boolean; error?: string }>({});
  async function add() {
    setState({ busy: true });
    try {
      await account.addPasskey(deviceName());
      onDone();
    } catch (err) {
      setState(cancelled(err) ? {} : { error: messageOf(err) });
    }
  }
  return (
    <div className="card" data-testid="new-passkey">
      <h2>Passkey für die neue Adresse</h2>
      <p>Ein Passkey gilt nur für die Adresse, unter der er angelegt wurde. Lege hier einen neuen an, dann meldest du dich ohne Passwort an.</p>
      {state.error && <p className="error-box">{state.error}</p>}
      <button className="primary wide" disabled={state.busy} onClick={() => void add()} data-testid="new-passkey-add">
        Passkey für die neue Adresse anlegen
      </button>
      <button className="link wide" onClick={onDone} data-testid="new-passkey-skip">
        Später
      </button>
    </div>
  );
}
